import CloseRounded from '@mui/icons-material/CloseRounded';
import SwapVertRounded from '@mui/icons-material/SwapVertRounded';
import { Box, Chip, MenuItem, TextField, Typography } from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { api, unwrap } from '../../api/client';
import { VariantPicker, type Variant } from '../../components/pickers';
import { DataTable, type Column } from '../../components/DataTable';
import { Money, Qty } from '../../components/Money';
import { EmptyState } from '../../components/states';
import { businessDate, dateTime } from '../../lib/format';
import { useShop } from '../../shop/ShopProvider';
import { InventoryLayout } from './InventoryLayout';
import {
  inventoryKeys,
  MOVEMENT_LABELS,
  MOVEMENT_TYPES,
  type MovementType,
  type StockMovement,
} from './model';
import { LocationSelect, SignedQty } from './shared';

const TONE: Record<string, 'success' | 'error' | 'info' | 'warning' | 'default'> = {
  OPENING: 'info',
  RECEIPT: 'success',
  SALES_RETURN: 'success',
  TRANSFER_IN: 'info',
  DELIVERY: 'default',
  PURCHASE_RETURN: 'warning',
  TRANSFER_OUT: 'info',
  ADJUSTMENT: 'warning',
  WRITE_OFF: 'error',
  COUNT_ADJUSTMENT: 'warning',
  PRODUCTION_CONSUME: 'default',
  PRODUCTION_OUTPUT: 'success',
};

export function MovementTypeChip({ type }: { type: string }) {
  const tone = TONE[type] ?? 'default';
  return (
    <Chip
      size="small"
      label={MOVEMENT_LABELS[type] ?? type}
      variant="outlined"
      sx={(t) => {
        if (tone === 'default') return {};
        const c = (t.vars ?? t).palette[tone].main;
        return {
          color: c,
          borderColor: `color-mix(in srgb, ${c} 30%, transparent)`,
          bgcolor: `color-mix(in srgb, ${c} 8%, transparent)`,
        };
      }}
    />
  );
}

export function MovementsPage() {
  const { format } = useShop();
  const [params, setParams] = useSearchParams();
  const [variant, setVariant] = useState<Variant | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const variantId = variant?.id ?? params.get('variantId') ?? '';
  const itemLabel = params.get('item');
  const locationId = params.get('locationId') ?? '';
  const type = (params.get('type') ?? '') as MovementType | '';
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';

  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
    setPage(1);
  };

  const query = {
    variantId: variantId || undefined,
    locationId: locationId || undefined,
    type: type || undefined,
    from: from || undefined,
    to: to || undefined,
    page,
    pageSize,
  };
  const movements = useQuery({
    queryKey: inventoryKeys.movements(query),
    queryFn: () => unwrap(api.GET('/stock/movements', { params: { query } })),
    placeholderData: keepPreviousData,
  });

  const seesCost = movements.data?.data.some((m) => m.unitCost !== null) ?? false;
  const columns: Column<StockMovement>[] = [
    {
      key: 'date',
      header: 'Date',
      width: 130,
      render: (m) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
            {businessDate(m.businessDate, format)}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {dateTime(m.postedAt, format)}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'item',
      header: 'Item',
      render: (m) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {m.sku}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {m.locationName}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      hideBelow: 'sm',
      render: (m) => <MovementTypeChip type={m.movementType} />,
    },
    {
      key: 'reason',
      header: 'Reason',
      hideBelow: 'lg',
      render: (m) => (
        <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 260 }}>
          {m.reason ?? '—'}
        </Typography>
      ),
    },
    { key: 'qty', header: 'Change', align: 'right', render: (m) => <SignedQty value={m.qty} /> },
    {
      key: 'balance',
      header: 'Balance',
      align: 'right',
      hideBelow: 'sm',
      render: (m) => <Qty value={m.balanceAfterQty} />,
    },
    ...(seesCost
      ? ([
          {
            key: 'value',
            header: 'Value',
            align: 'right',
            hideBelow: 'md',
            render: (m) => <Money value={m.totalCost} signed />,
          },
        ] satisfies Column<StockMovement>[])
      : []),
  ];

  const filtered = Boolean(variantId || locationId || type || from || to);

  return (
    <InventoryLayout subtitle="Every change to stock, newest first — receipts, deliveries, returns, adjustments and counts.">
      <DataTable
        columns={columns}
        rows={movements.data?.data}
        rowKey={(m) => m.id}
        loading={movements.isLoading}
        page={movements.data?.page}
        onPageChange={setPage}
        onPageSizeChange={(s) => {
          setPageSize(s);
          setPage(1);
        }}
        toolbar={
          <>
            <Box sx={{ minWidth: { sm: 260 }, flex: { sm: 1 }, maxWidth: { sm: 340 } }}>
              {itemLabel && !variant && variantId ? (
                <Chip
                  label={itemLabel}
                  onDelete={() => {
                    const next = new URLSearchParams(params);
                    next.delete('variantId');
                    next.delete('item');
                    setParams(next, { replace: true });
                  }}
                  deleteIcon={<CloseRounded />}
                  sx={{ maxWidth: '100%', height: 40, borderRadius: '10px', fontWeight: 600 }}
                />
              ) : (
                <VariantPicker
                  value={variant}
                  onChange={(v) => {
                    setVariant(v);
                    setPage(1);
                  }}
                  label="Item"
                />
              )}
            </Box>
            <LocationSelect
              value={locationId}
              onChange={(v) => set('locationId', v)}
              allLabel="All locations"
              onlyActive={false}
            />
            <TextField
              select
              label="Type"
              value={type}
              onChange={(e) => set('type', e.target.value)}
              sx={{ minWidth: 170 }}
            >
              <MenuItem value="">All types</MenuItem>
              {MOVEMENT_TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {MOVEMENT_LABELS[t]}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              type="date"
              label="From"
              value={from}
              onChange={(e) => set('from', e.target.value)}
              slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: to || undefined } }}
            />
            <TextField
              type="date"
              label="To"
              value={to}
              onChange={(e) => set('to', e.target.value)}
              slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: from || undefined } }}
            />
          </>
        }
        empty={
          <EmptyState
            icon={<SwapVertRounded />}
            title={filtered ? 'No movements match' : 'No stock movements yet'}
            body={
              filtered
                ? 'Try a wider date range or clear a filter.'
                : 'Movements appear here as soon as stock is received, sold, adjusted or counted.'
            }
          />
        }
      />
    </InventoryLayout>
  );
}
